import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { useHomepageTiles } from '../../../common/Experiments/HomepageTilesExperiment';
import { SearchPromotionItem } from '../../../common/components/SearchHeader/Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { useOnClickOutside } from '../../../common/hooks/useOnClickOutside';
import { useRouter } from '../../../common/hooks/useRouter';
import { addQueryParams } from '../../../common/routing/addQueryParams';
import { take } from '../../../common/util/take';
import { GPHomepageCard, GPButtonTypes } from '../GPHomepageCard';
import { GPHomepageCardsContainer } from '../GPHomepageCard.styles';
import {
  SearchSpecialtySelectorWrapper,
  SearchSpecialtyButtonContainer,
  DesktopHideWrapper,
  MobileHideWrapper,
} from './SearchSpecialtySelector.styles';
import { SpecialtyButton } from './SpecialtyButton';
import { OverFlowSpecialtyButton } from './SpecialtyOverflowMenu/SpecialtyOverflowButton';
import { SpecialtyOverflowMenu } from './SpecialtyOverflowMenu/SpecialtyOverflowMenu';

interface SearchSpecialtySelectorProps {
  promotedSpecialties: SearchPromotionItem[];
  setActiveSpecialtyIndex: Dispatch<number>;
  activeSpecialtyIndex?: number;
  visibleIcons?: number;
  onSpecialtyChange?: (specialty: SearchPromotionItem | null) => void;
  renderGPCards?: boolean;
  renderOtherTiles?: boolean;
}

export const SearchSpecialtySelector = ({
  promotedSpecialties,
  visibleIcons = 5,
  setActiveSpecialtyIndex,
  activeSpecialtyIndex = -1,
  onSpecialtyChange,
  renderGPCards = false,
  renderOtherTiles = true,
}: SearchSpecialtySelectorProps) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const dropDownRef = useRef<HTMLDivElement>(null);

  const homepageTilesExperiment = useHomepageTiles();
  const visibleIconsLocal = homepageTilesExperiment ? visibleIcons + 2 : visibleIcons;
  const router = useRouter();

  useEffect(() => {
    if (activeSpecialtyIndex === -1) setSelectedOverflowSpecialty(null);
  }, [activeSpecialtyIndex]);

  const [
    selectedOverflowSpecialty,
    setSelectedOverflowSpecialty,
  ] = useState<SearchPromotionItem | null>(null);

  const onSelectSpecialty = (specialty: SearchPromotionItem | null, i: number) => {
    const nextActiveIndex = activeSpecialtyIndex === i ? -1 : i;

    setShowMore(false);
    setActiveSpecialtyIndex(nextActiveIndex);

    // We can't call this for the Telehealth card, otherwise the style doesn't change
    // on click
    specialty?.type !== 'telehealth' &&
      onSpecialtyChange?.(activeSpecialtyIndex !== i ? specialty : null);

    if (nextActiveIndex < overflowSpecialties.length) setSelectedOverflowSpecialty(null);
  };

  const onSelectOverFlowSpecialty = (specialty: SearchPromotionItem | null, i: number) => {
    onSelectSpecialty(specialty, i);
    setSelectedOverflowSpecialty(specialty);
  };

  const overflowActive =
    activeSpecialtyIndex > visibleIconsLocal - 1 && activeSpecialtyIndex !== -1;

  const overflowLabel =
    selectedOverflowSpecialty?.topNavTitle ??
    selectedOverflowSpecialty?.title ??
    (showMore ? 'Show Less' : 'Show More');

  const overflowAltLabel =
    selectedOverflowSpecialty?.altTitle ?? (showMore ? 'Show Less' : 'Show More');

  const overflowLabelExperiment =
    selectedOverflowSpecialty?.topNavTitle ??
    selectedOverflowSpecialty?.title ??
    (showMore ? 'See Less' : 'See More');

  const overflowAltLabelExperiment =
    selectedOverflowSpecialty?.altTitle ??
    (showMore
      ? homepageTilesExperiment
        ? 'See less services'
        : 'See Less'
      : homepageTilesExperiment
      ? 'See more services'
      : 'See More');

  const overflowSpecialties = useMemo(
    () => promotedSpecialties.slice(visibleIconsLocal, promotedSpecialties.length),
    [promotedSpecialties, visibleIconsLocal],
  );

  useOnClickOutside(dropDownRef, () => setShowMore(false));

  if (!promotedSpecialties.length) return null;

  const generalPracticeSpecialty = promotedSpecialties.find(s => s.title === 'General Practice');
  const telehealthSpecialty = promotedSpecialties.find(s => s.type === 'telehealth');

  if (!generalPracticeSpecialty || !telehealthSpecialty) return null;

  const redirectToTelehealthServicesPage = () => {
    const url = addQueryParams(`https://services.healthengine.com.au/telehealth-gp-options`, {
      source: 'hp',
    });

    void router?.push(url);
  };

  const SearchSpecialtyContainerComponent = (
    <SearchSpecialtyButtonContainer experiment={homepageTilesExperiment}>
      {!!promotedSpecialties.length &&
        take(promotedSpecialties, visibleIconsLocal).map((s, i) =>
          homepageTilesExperiment &&
          (s.title === 'General Practice' || s.type === 'telehealth') ? null : (
            <SpecialtyButton
              {...s}
              title={s.type === 'telehealth' ? 'GP Telehealth' : s.topNavTitle ?? s.title}
              key={i}
              active={activeSpecialtyIndex === i}
              setActive={() => {
                s.type === 'telehealth'
                  ? redirectToTelehealthServicesPage()
                  : onSelectSpecialty(s, i);
              }}
              badge={s.type === 'telehealth' ? 'Open 24/7' : s.badge}
            />
          ),
        )}
      {!!overflowSpecialties.length && (
        <OverFlowSpecialtyButton
          label={homepageTilesExperiment ? overflowLabelExperiment : overflowLabel}
          altLabel={homepageTilesExperiment ? overflowAltLabelExperiment : overflowAltLabel}
          onClick={() => setShowMore(!showMore)}
          open={showMore}
          active={overflowActive}
          icon={selectedOverflowSpecialty?.icon}
          isDesktop={renderGPCards}
        />
      )}
    </SearchSpecialtyButtonContainer>
  );

  return (
    <SearchSpecialtySelectorWrapper ref={dropDownRef}>
      {renderGPCards && (
        <>
          <GPHomepageCardsContainer>
            <GPHomepageCard
              type={GPButtonTypes.Telehealth}
              onSelectSpecialty={onSelectSpecialty}
              specialty={telehealthSpecialty}
              active={activeSpecialtyIndex === GPButtonTypes.Telehealth}
            />
            <GPHomepageCard
              type={GPButtonTypes.GP}
              onSelectSpecialty={onSelectSpecialty}
              specialty={generalPracticeSpecialty}
              active={activeSpecialtyIndex === GPButtonTypes.GP}
            />
          </GPHomepageCardsContainer>
          {homepageTilesExperiment ? (
            <MobileHideWrapper>{SearchSpecialtyContainerComponent}</MobileHideWrapper>
          ) : (
            SearchSpecialtyContainerComponent
          )}
        </>
      )}
      {renderOtherTiles &&
        (homepageTilesExperiment ? (
          <DesktopHideWrapper>{SearchSpecialtyContainerComponent}</DesktopHideWrapper>
        ) : (
          SearchSpecialtyContainerComponent
        ))}
      {!!overflowSpecialties.length && showMore && (
        <SpecialtyOverflowMenu
          overflowItems={overflowSpecialties}
          onSpecialtyChange={onSelectOverFlowSpecialty}
          totalItems={promotedSpecialties?.length}
          activeSpecialtyIndex={activeSpecialtyIndex}
          onBlur={() => setShowMore(false)}
        />
      )}
    </SearchSpecialtySelectorWrapper>
  );
};
